// Playback-движок аватара Диалога: единая очередь видео/TTS, double-buffer
// видеослоёв, idle-состояние, HQ-индикатор. Источники idle/face-медиа активной
// категории подключаются late-bound через configureAvatarPlayback — граф
// модулей остаётся ацикличным. Выделено из avg-dialog.ts (аудит, этап 2).
import { $ } from '../shared/index';
import { interruptHeyGenAvatar, isHeyGenAvatarActive } from './avg-dialog-heygen';

/** Optional per-item hooks — captions_mode=after_playback (avg-dialog.ts) uses
 * onEnded to sync a unit's text reveal to the end of its own clip/audio;
 * onStart is symmetric (e.g. future per-unit UI state) though unused today. */
type PlaybackCallbacks = { onStart?: () => void; onEnded?: () => void };

// One ordered playback queue for a turn: each sentence is either a talking-head
// video clip (audio muxed in) or an audio-only TTS fallback. A SINGLE player
// consumes them strictly one at a time so a video's muxed voice and a fallback
// TTS voice never overlap (the previous design ran independent video and audio
// chains that played concurrently → two voices when a turn mixed both).
type PlaybackItem = { kind: 'video' | 'audio'; url: string } & PlaybackCallbacks;
let playbackQueue: PlaybackItem[] = [];
let playbackBusy = false;
let currentAudio: HTMLAudioElement | null = null;
let isSpeaking = false;
// Two stacked <video> layers double-buffer consecutive video clips: while the
// active layer plays clip N, the inactive one preloads clip N+1, and on `ended`
// we swap which layer is visible — no empty-frame flash between clips.
let activeClipIdx: 0 | 1 = 0;
/**
 * Layer that OWNS the current clip (set synchronously in playVideoItem before
 * play(), unlike activeClipIdx which swaps only after play() resolves). The
 * preloader must target the OTHER layer — keying it off activeClipIdx raced:
 * a clip arriving before the previous play() resolved preloaded into the very
 * layer that was starting, replacing its src and killing the first sentence.
 */
let playingIdx: 0 | 1 | null = null;
/** URL already loaded into the inactive (back) layer, if any. */
let preloadedUrl: string | null = null;

/** The layer the NEXT clip should load into: the one not owning the current clip. */
/**
 * Браузер отказал в запуске со звуком (autoplay policy) — ждём касания.
 *
 * Раньше отказ обрабатывался как конец клипа: очередь ехала дальше, и от
 * ответа человеку доставался один текст. Со стороны это «аватар перестал
 * говорить и отвечает только текстом» — ровно та жалоба, с которой пришли из
 * Mini App Telegram (2026-08-10): во встроенном вебвью разговор идёт голосом,
 * то есть касаний, дающих право на звук, может не быть вовсе.
 *
 * Ждать касания честнее, чем терять реплику: клип остаётся на месте, а первое
 * же касание экрана его запускает.
 */
let soundHintShown = false;

/**
 * Отказ в звуке НЕ имеет права остановить ход.
 *
 * Первая попытка чинить это ждала касания и держала клип — и ход вставал
 * колом: при `captions_mode=after_playback` текст ответа раскрывается по концу
 * воспроизведения, поэтому человек оставался с вечным «Думаю…» (прод
 * 2026-08-10). Ход идёт дальше, а касание всего лишь возвращает звук
 * СЛЕДУЮЩИМ репликам.
 */
function noteSoundBlocked(): void {
  const stage = document.querySelector('.adlg-avatar-stage');
  const hint = $('adlg-hq-indicator');
  if (hint) {
    hint.textContent = 'Коснитесь экрана — включу звук';
    (hint as HTMLElement).hidden = false;
  }
  if (!stage || soundHintShown) return;
  soundHintShown = true;
  stage.addEventListener(
    'click',
    () => {
      // Касание — законный повод взять право на звук для будущих клипов.
      primePlaybackWithGesture();
      const el = $('adlg-hq-indicator');
      if (el && el.textContent === 'Коснитесь экрана — включу звук') {
        el.textContent = 'Готовится видео ответа…';
        (el as HTMLElement).hidden = true;
      }
      soundHintShown = false;
    },
    { once: true },
  );
}

/**
 * Разрешение на звук берётся ОДИН раз — на первом касании человека.
 *
 * Политика автозапуска смотрит не на «сайт хороший», а на то, было ли действие
 * пользователя. Ответ аватара приходит через секунды после нажатия «Голос», и к
 * этому моменту разрешение уже «остыло»: клип отказывался играть, и человеку
 * приходилось касаться экрана на КАЖДЫЙ ответ (жалоба владельца 2026-08-10 —
 * «отвечает голосом, но только после нажатия»).
 *
 * Приём стандартный: прямо внутри жеста дать каждому слою беззвучно
 * стартовать и тут же остановиться. Элемент считается запущенным человеком, и
 * дальше играет по команде кода — уже со звуком.
 */
export function primePlaybackWithGesture(): void {
  for (const idx of [0, 1] as const) {
    const layer = getClipLayer(idx);
    if (!layer) continue;
    // Пустому слою играть нечего — play() отклонится, и права на звук он не
    // получит. А клип придёт как раз в него: слои чередуются. Поэтому пустому
    // подкладываем idle-видео (оно уже загружено) — этого хватает, чтобы
    // элемент считался запущенным человеком.
    if (!layer.getAttribute('src')) {
      const idle = media.idleVideoUrl();
      if (idle) layer.src = idle;
    }
    // Состояние слоя возвращаем ровно то, что было: на одном из них может
    // крутиться idle-петля, и остановить её «на всякий случай» значит
    // заморозить аватара между ходами.
    const wasMuted = layer.muted;
    const wasPaused = layer.paused;
    layer.muted = true;
    void layer
      .play()
      .then(() => {
        if (wasPaused) layer.pause();
        layer.muted = wasMuted;
      })
      .catch(() => {
        layer.muted = wasMuted;
      });
  }
}

function nextClipLayerIdx(): 0 | 1 {
  if (playingIdx !== null) return playingIdx === 0 ? 1 : 0;
  return activeClipIdx === 0 ? 1 : 0;
}

type PlaybackMediaSources = {
  /** Idle-видео активной категории (или idle_animation). */
  idleVideoUrl(): string | undefined;
  /** Статичное фото аватара активной категории. */
  faceUrl(): string | undefined;
  /** Заставка — всегда видимая подложка окна видео (слой ниже фото/видео). */
  splashUrl(): string | undefined;
};

let media: PlaybackMediaSources = {
  idleVideoUrl: () => undefined,
  faceUrl: () => undefined,
  splashUrl: () => undefined,
};

/** Подключение источников idle/face-медиа (avg-dialog.ts) на module evaluation. */
export function configureAvatarPlayback(sources: PlaybackMediaSources): void {
  media = sources;
}

/** Голос аватара сейчас звучит (для barge-in). */
export function isAvatarSpeaking(): boolean {
  return isSpeaking;
}

export function getAvatarVideo(): HTMLVideoElement | null {
  return $('adlg-avatar-video') as HTMLVideoElement | null;
}

/** Returns one of the two stacked clip layers (0 = primary, also used for the
 * idle loop and HeyGen; 1 = the secondary double-buffer layer). */
function getClipLayer(idx: 0 | 1): HTMLVideoElement | null {
  return $(idx === 0 ? 'adlg-avatar-video' : 'adlg-avatar-video-b') as HTMLVideoElement | null;
}

function setVideoActive(video: HTMLVideoElement, active: boolean): void {
  video.classList.toggle('adlg-video-active', active);
}

/** Shows/hides the static face image layer (idle state when there is no video). */
function setFaceVisible(show: boolean): void {
  const face = $('adlg-avatar-face') as HTMLImageElement | null;
  if (!face) return;
  const url = media.faceUrl();
  if (show && url) {
    if (face.getAttribute('src') !== url) face.src = url;
    face.classList.remove('hidden');
  } else {
    face.classList.add('hidden');
  }
}

/**
 * Idle avatar state: idle video loop if configured, otherwise the avatar's face
 * photo, otherwise the emoji placeholder. Called on load and after each answer
 * so the user sees the face rather than an empty video frame.
 */
export function setAvatarVideo(url?: string): void {
  const video = getAvatarVideo();
  const secondary = getClipLayer(1);
  const placeholder = $('adlg-avatar-placeholder');
  if (!video) return;

  // Idle/HeyGen always live on layer 0; fade the secondary clip layer out
  // (it keeps its last decoded frame invisibly until the next preload).
  if (secondary && !playbackBusy) setVideoActive(secondary, false);

  if (isHeyGenAvatarActive()) {
    video.hidden = false;
    setVideoActive(video, true);
    setFaceVisible(false);
    placeholder?.classList.add('hidden');
    return;
  }

  if (url) {
    video.hidden = false;
    setVideoActive(video, true);
    setFaceVisible(false);
    placeholder?.classList.add('hidden');
    if (video.src !== url) {
      video.src = url;
      video.loop = true;
      void video.play().catch(() => undefined);
    } else if (video.paused) {
      // Возврат в idle после ответа. playVideoItem паузит слой 0 и снимает с
      // него loop, отдавая сцену клипу с липсинком; src при этом не меняется,
      // поэтому прежняя ветка `src !== url` сюда не заходила — и петля idle
      // после первого же ответа замирала на последнем кадре.
      video.loop = true;
      video.muted = true;
      void video.play().catch(() => undefined);
    }
  } else {
    video.hidden = true;
    setVideoActive(video, false);
    video.removeAttribute('src');
    // Эмодзи-заглушка — последний резерв: при заданной заставке или фото
    // сцена уже не пустая, и поверх них эмодзи не рисуем.
    const hasFace = !!media.faceUrl();
    const hasSplash = !!media.splashUrl();
    setFaceVisible(true);
    placeholder?.classList.toggle('hidden', hasFace || hasSplash);
  }
}

/** Premium tier: pill over the avatar stage while the HQ video renders. */
export function setHqIndicator(visible: boolean): void {
  const el = $('adlg-hq-indicator');
  if (el) (el as HTMLElement).hidden = !visible;
}

function stopAudioQueue(): void {
  if (currentAudio) {
    currentAudio.onended = null;
    currentAudio.onerror = null;
    currentAudio.pause();
    currentAudio = null;
  }
  isSpeaking = false;
}

function stopAvatarVideo(): void {
  for (const idx of [0, 1] as const) {
    const video = getClipLayer(idx);
    if (!video) continue;
    video.onended = null;
    video.onerror = null;
    video.pause();
    try {
      video.currentTime = 0;
    } catch {
      // ignore — element may not have a src yet
    }
  }
  preloadedUrl = null;
  playingIdx = null;
}

/** Stops any queued/playing TTS audio and avatar video clips. Used both by
 * explicit barge-in and by session teardown/reset. */
export function stopLivePlayback(): void {
  playbackQueue = [];
  playbackBusy = false;
  stopAudioQueue();
  stopAvatarVideo();
  setHqIndicator(false);
  interruptHeyGenAvatar();
  // Иначе сцена остаётся на последнем кадре прерванного клипа.
  setAvatarVideo(media.idleVideoUrl());
}

/** Enqueues a per-sentence talking-head clip (audio muxed in). `callbacks.onEnded`
 * fires once THIS clip finishes playing (captions_mode=after_playback sync). */
export function enqueueAvatarVideo(url: string, callbacks?: PlaybackCallbacks): void {
  enqueuePlayback({ kind: 'video', url, ...callbacks });
}

/** Enqueues an audio-only TTS clip (fallback for a sentence that didn't render). */
export function enqueueTtsAudio(url: string, callbacks?: PlaybackCallbacks): void {
  enqueuePlayback({ kind: 'audio', url, ...callbacks });
}

/**
 * Pushes one item onto the single ordered playback queue and starts the player
 * if idle. Items play strictly one at a time, in arrival (sentence) order —
 * video and audio never overlap, so there is only ever one voice.
 */
function enqueuePlayback(item: PlaybackItem): void {
  playbackQueue.push(item);
  if (!playbackBusy) advancePlayback();
  else if (item.kind === 'video') preloadNextClip();
}

/** Loads the next queued clip into the inactive (back) layer so the swap in
 * playVideoItem starts from an already-decoded first frame — no flash. Only
 * previews when the NEXT item is a video; never touches the current clip's
 * layer (see nextClipLayerIdx). */
function preloadNextClip(): void {
  const next = playbackQueue[0];
  if (!next || next.kind !== 'video' || preloadedUrl) return;
  const back = getClipLayer(nextClipLayerIdx());
  if (!back) return;
  back.loop = false;
  back.preload = 'auto';
  back.src = next.url;
  back.load();
  preloadedUrl = next.url;
}

/** Consumes the next queued item; on drain restores the idle face/loop. */
function advancePlayback(): void {
  const item = playbackQueue.shift();
  if (!item) {
    // Queue drained: stop audio, fade the last clip layer out, restore idle.
    playbackBusy = false;
    isSpeaking = false;
    preloadedUrl = null;
    if (currentAudio) {
      currentAudio.onended = null;
      currentAudio.onerror = null;
      currentAudio = null;
    }
    const lastIdx = playingIdx ?? activeClipIdx;
    const last = getClipLayer(lastIdx);
    if (last) {
      last.onended = null;
      last.onerror = null;
      if (lastIdx !== 0) setVideoActive(last, false);
      last.muted = true;
      last.loop = true;
    }
    playingIdx = null;
    activeClipIdx = 0;
    setAvatarVideo(media.idleVideoUrl());
    return;
  }

  playbackBusy = true;
  isSpeaking = true;
  item.onStart?.();
  if (item.kind === 'audio') {
    playAudioItem(item);
  } else {
    playVideoItem(item);
  }
}

/** Calls advancePlayback() to move the queue forward, then item.onEnded — in
 * that order, so a callback checking isAvatarSpeaking()/the queue sees the
 * POST-advance state (e.g. "was this the last queued item?"). */
function endItemAndAdvance(item: PlaybackItem): void {
  advancePlayback();
  item.onEnded?.();
}

/** Plays one audio-only clip; on end advances the unified queue. */
function playAudioItem(item: PlaybackItem): void {
  const audio = new Audio(item.url);
  currentAudio = audio;
  audio.onended = () => { currentAudio = null; endItemAndAdvance(item); };
  audio.onerror = () => { currentAudio = null; endItemAndAdvance(item); };
  void audio.play().catch(() => { currentAudio = null; endItemAndAdvance(item); });
}

/** Plays one talking-head clip on the double-buffered video layers; on end
 * advances the unified queue. */
function playVideoItem(item: PlaybackItem): void {
  const url = item.url;
  const backIdx = nextClipLayerIdx();
  const back = getClipLayer(backIdx);
  const front = getClipLayer(backIdx === 0 ? 1 : 0);
  if (!back) { endItemAndAdvance(item); return; }

  // Claim the layer synchronously so the preloader targets the OTHER layer even
  // while play() is still resolving.
  playingIdx = backIdx;
  back.hidden = false;
  back.loop = false;
  back.muted = false; // the clip carries this sentence's voice
  if (preloadedUrl !== url) back.src = url;
  preloadedUrl = null;
  back.onended = () => endItemAndAdvance(item);
  back.onerror = () => endItemAndAdvance(item);
  void back
    .play()
    .then(() => {
      // Swap layers only once the new clip is actually rendering frames; until
      // then the front layer keeps showing the previous clip's last frame.
      setVideoActive(back, true);
      activeClipIdx = backIdx;
      // Нижние слои гасим ТОЛЬКО здесь. Раньше фото и заглушка прятались до
      // play(): новый слой ещё прозрачный (opacity 0), старый уже погашен —
      // и на время загрузки клипа сцена оставалась пустой. Это и был провал
      // в чёрное между idle и видео с липсинком.
      setFaceVisible(false);
      $('adlg-avatar-placeholder')?.classList.add('hidden');
      if (front && front !== back) {
        setVideoActive(front, false);
        front.onended = null;
        front.onerror = null;
        front.pause();
        front.muted = true;
      }
      preloadNextClip();
    })
    .catch((error: unknown) => {
      // Звук не дали — говорим об этом на сцене и идём дальше. Держать клип
      // нельзя: текст ответа в режиме after_playback ждёт конца воспроизведения.
      if ((error as { name?: string } | null)?.name === 'NotAllowedError') noteSoundBlocked();
      endItemAndAdvance(item);
    });
}
